import { Box, SxProps, Theme, LinearProgress } from '@mui/material';
import Card from '../Utils/Card';
import { useScreenLargerThan } from '../../lib/hooks/breakpoint-hooks';
import { DataGrid, GridColDef, GridToolbarContainer, GridToolbarExport } from '@mui/x-data-grid';
import { ReportingModel } from '../../lib/models/ReportingModel';
import FilterByDatePicker from './FilterByDatePicker';
import { CustomDateHourCell } from '../../lib/helper/time-formatter';
import DatagridNoRowsComponent from '../DatagridNoRowsComponent';

export type ReportingParams = {
    caseNumber: string;
    startDate: Date;
    endDate: Date;
};

export type ReportingListingProps = {
    data: ReportingModel[];
    inputDateProps: InputDateProps;
    loading: boolean;
};

export type InputDateProps = {
    startDate: string;
    endDate: string;
    // eslint-disable-next-line
    setStartDate: any;
    // eslint-disable-next-line
    setEndDate: any;
};

const columns: GridColDef[] = [
    { field: 'caseNumber', headerName: 'Case Number', minWidth: 150, flex: 1 },
    { field: 'status', headerName: 'Status', minWidth: 120, flex: 1 },
    { field: 'logType', headerName: 'Type', minWidth: 120, flex: 1 },
    { field: 'logMessage', headerName: 'Message', minWidth: 180, flex: 1 },
    { field: 'medicalReviewerName', headerName: 'Medical Reviewer', minWidth: 200, flex: 1 },
    { field: 'cosignerName', headerName: 'Co-Signer', minWidth: 120, flex: 1 },
    { field: 'nurseName', headerName: 'Nurse', minWidth: 120, flex: 1 },
    { field: 'caseStage', headerName: 'Stage', minWidth: 180, flex: 1 },
    { field: 'clientName', headerName: 'Client', minWidth: 120, flex: 1 },
    {
        field: 'attemptDate',
        headerName: 'Date of Attempt',
        minWidth: 120,
        flex: 1,
        renderCell: (params) => (
            <CustomDateHourCell
                timeValue={params.row.formattedAttemptTime}
                dateValue={params.row.formattedAttemptDate}
            />
        )
    }
];

function CustomToolbar({ startDate, setStartDate, endDate, setEndDate }: InputDateProps) {
    const isLargerThanSmall = useScreenLargerThan('sm');

    return (
        <GridToolbarContainer
            sx={
                isLargerThanSmall
                    ? { display: 'flex', alignItems: 'center', justifyContent: 'space-between' }
                    : { display: 'block' }
            }>
            <Card.Title>Reporting</Card.Title>
            <Box sx={isLargerThanSmall ? { display: 'flex' } : { display: 'inline' }}>
                <FilterByDatePicker
                    startDate={startDate}
                    setStartDate={setStartDate}
                    endDate={endDate}
                    setEndDate={setEndDate}
                />
                <GridToolbarExport />
            </Box>
        </GridToolbarContainer>
    );
}

const Reporting = ({ data, inputDateProps, loading }: ReportingListingProps) => {
    const isLargerThanSmall = useScreenLargerThan('sm');

    return (
        <Card sx={cardStyle(isLargerThanSmall)}>
            <LinearProgress sx={{ visibility: loading ? 'visible' : 'hidden', height: '3px' }} />
            <Card.Body>
                <DataGrid
                    sx={{ border: 0, padding: 1 }}
                    rows={data}
                    columns={columns}
                    autoHeight
                    pagination
                    slots={{
                        toolbar: CustomToolbar,
                        noRowsOverlay: DatagridNoRowsComponent
                    }}
                    slotProps={{
                        toolbar: {
                            startDate: inputDateProps.startDate,
                            setStartDate: inputDateProps.setStartDate,
                            endDate: inputDateProps.endDate,
                            setEndDate: inputDateProps.setEndDate
                        }
                    }}
                />
            </Card.Body>
        </Card>
    );
};

export default Reporting;

const cardStyle: (media: boolean) => SxProps<Theme> = (media: boolean) => {
    const biggerThanSm: SxProps<Theme> = { padding: 0, overflow: 'hidden', width: '100%' };
    const smallerThanSm: SxProps<Theme> = {
        padding: 0,
        overflow: 'hidden',
        borderRadius: 0
    };

    return media ? biggerThanSm : { ...biggerThanSm, ...smallerThanSm };
};
